/**
 * ui/SeedStore — « Magasin de seeds » embarqué dans l'admin.
 * @author Dr Hamid MADANI <drmdh@msn.com>
 *
 * Catalogue de configurations d'agence prêtes à l'emploi, présent dans `admin.exe`
 * ET sur la console centrale (ticket-admin.amia.fr, même SPA). Actions :
 *   • Appliquer  → écrase la config du SITE CIBLÉ via `api.saveConfig` (PUT /api/config).
 *     En mode console, `api` pointe déjà le site distant relayé — donc « ce site »
 *     = le site sélectionné dans la console.
 *   • Télécharger → export du seed en `agency-*.json`.
 *   • Importer / Capturer → enrichit le catalogue (persisté localement).
 * Aucune dépendance serveur : le catalogue vit côté admin (offline).
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import ApercuArticle from './ApercuArticle';
import {
  buildCatalog, loadUserSeeds, chargerMagasin, ajouterArticle, retirerArticle, lireArticleComplet, isSeedShape,
} from '../ticketflow/seedCatalog';

function downloadJson(filename, data) {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export default function SeedStore({ api, reload }) {
  // Affichage IMMÉDIAT depuis le cache local, puis remplacement par le magasin de la
  // console dès qu'il répond. Attendre le réseau montrerait un catalogue vide à chaque
  // ouverture de l'onglet, y compris quand rien n'a changé.
  const [userSeeds, setUserSeeds] = useState(() => loadUserSeeds());
  const [preview, setPreview] = useState(null); // entrée en cours de prévisualisation
  const [status, setStatus] = useState('');
  const [busy, setBusy] = useState('');
  const fileRef = useRef(null);

  useEffect(() => {
    let vivant = true;
    chargerMagasin().then((e) => { if (vivant) setUserSeeds(e); }).catch(() => { /* cache conservé */ });
    return () => { vivant = false; };
  }, []);

  const catalog = useMemo(() => buildCatalog(userSeeds), [userSeeds]);

  const apply = async (entry) => {
    if (!window.confirm(
      `Appliquer « ${entry.name} » ?\n\nCela REMPLACE la configuration active du site ciblé `
      + `(services, guichets, affichage…). Les tickets en cours peuvent être affectés.`,
    )) return;
    setBusy(entry.filename); setStatus('');
    try {
      // Retirer les profils d'affichage de l'arbre config avant le PUT, puis les appliquer à part.
      const { __displayProfiles, ...config } = entry.data || {};
      await api.saveConfig(config);
      if (Array.isArray(__displayProfiles) && __displayProfiles.length) {
        try { await api.applyDisplaysSeed?.(__displayProfiles); } catch { /* serveur de site sans /displays/seed */ }
      }

      // Les COMPTES ne vivent pas dans la configuration : sans cet appel, l'article
      // remplaçait services, guichets et affichage, mais laissait en place le personnel
      // du client précédent — un laboratoire annonçait « Guichet État civil 1 », compte
      // d'une mairie installée des mois plus tôt. Rien n'est supprimé : les comptes du
      // site absents de l'article sont NOMMÉS, à l'administrateur de trancher.
      let comptes = '';
      if (Array.isArray(config.users) && config.users.length && api.seedUsers) {
        try {
          const r = await api.seedUsers(config.users);
          const bouts = [];
          if (r.crees?.length) bouts.push(`${r.crees.length} compte(s) créé(s)`);
          if (r.ignores?.length) bouts.push(`${r.ignores.length} déjà présent(s)`);
          if (r.refuses?.length) bouts.push(`${r.refuses.length} refusé(s)`);
          if (r.horsArticle?.length) {
            bouts.push(
              `${r.horsArticle.length} compte(s) du site hors article à vérifier : `
              + r.horsArticle.map((u) => u.fullName || u.username).join(', '),
            );
          }
          comptes = bouts.length ? ` — ${bouts.join(' · ')}` : '';
        } catch (e) {
          // Serveur de site pas encore à jour (route absente) : la configuration, elle,
          // est bien appliquée. On le dit sans transformer ça en échec global.
          comptes = ' — comptes NON appliqués (serveur de site à mettre à jour)';
        }
      }

      await reload?.();
      setStatus(`✓ « ${entry.name} » appliqué au site.${comptes}`);
    } catch (e) {
      // Un article qui « ne s'applique pas » a des causes très différentes, et le message
      // brut d'axios (« Request failed with status code 503 ») n'en désigne aucune. Le
      // 503 est de loin la plus fréquente : on tente d'appliquer sur un site qui n'est
      // pas connecté à la console, ce qui n'a rien d'une panne.
      const code = e?.response?.status;
      setStatus(
        code === 503 ? 'Site HORS LIGNE : il n\'est pas connecté à la console. Rien n\'a été modifié.'
        : code === 403 ? 'Accès refusé (rôle insuffisant).'
        : code === 413 ? 'Article trop volumineux pour être relayé — signalez-le, le plafond est réglable.'
        : code === 401 ? 'Session expirée : reconnectez-vous à la console.'
        : `Échec de l'application : ${e?.message || e}`,
      );
    } finally { setBusy(''); }
  };

  const importFile = async (file) => {
    if (!file) return;
    setStatus('');
    try {
      const parsed = JSON.parse(await file.text());
      const data = parsed && parsed.config ? parsed.config : parsed; // tolérant (ancien emballage)
      if (!isSeedShape(data)) throw new Error('Format invalide : `agency` et `serviceTypes` requis.');
      setUserSeeds(await ajouterArticle(data, file.name));
      setStatus(`✓ Seed « ${data.agency?.name || file.name} » ajouté au catalogue.`);
    } catch (e) {
      setStatus(`Import invalide : ${e?.message || e}`);
    } finally {
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  // Capture = BACKUP complet du site actif : `getConfig` renvoie l'arbre `data`
  // ENTIER (styles, thèmes, affichage, voix, pub, services…), donc réplicable à
  // l'identique sur un autre site OU restaurable en cas de problème serveur. On
  // l'ajoute au catalogue ET on le télécharge sur disque (backup hors localStorage).
  const captureActive = async () => {
    setStatus('');
    try {
      const cfg = await api.getConfig();
      if (!isSeedShape(cfg)) throw new Error('La config du site ne ressemble pas à un seed d\'agence.');
      // Joindre les PROFILS d'affichage (par nom, HORS arbre config) — retirés à l'apply.
      try { const dp = await api.getDisplaysSeed?.(); if (Array.isArray(dp) && dp.length) cfg.__displayProfiles = dp; } catch { /* serveur sans seed d'affichages */ }
      const slug = String(cfg.agency?.name || 'site').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'site';
      const d = new Date();
      const stamp = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}-${String(d.getHours()).padStart(2, '0')}${String(d.getMinutes()).padStart(2, '0')}`;
      const filename = `agency-${slug}-backup-${stamp}.json`;
      setUserSeeds(await ajouterArticle(cfg, filename));
      downloadJson(filename, cfg); // backup sur disque immédiat
      setStatus(`✓ Backup complet du site « ${cfg.agency?.name || slug} » créé (ajouté au catalogue + fichier ${filename} téléchargé).`);
    } catch (e) {
      setStatus(`Capture impossible : ${e?.message || e}`);
    }
  };

  // GLISSER-DÉPOSER : lâcher un `.json` sur le magasin l'y range. C'est le geste naturel
  // quand on rapatrie un article modifié chez un client — le passage par « Importer… » ne
  // sert plus qu'à ceux qui préfèrent le sélecteur de fichiers.
  const [survol, setSurvol] = useState(false);

  const onDrop = async (e) => {
    e.preventDefault();
    setSurvol(false);
    const fichiers = [...(e.dataTransfer?.files || [])].filter((f) => /\.json$/i.test(f.name));
    if (!fichiers.length) { setStatus('Déposez un fichier .json de configuration.'); return; }
    // On accepte un LOT : rapatrier plusieurs articles d'un coup est courant après une
    // tournée chez un client.
    for (const f of fichiers) await importFile(f);
    if (fichiers.length > 1) setStatus(`✓ ${fichiers.length} articles ajoutés au magasin.`);
  };

  // L'aperçu affiche le VRAI logo : il faut donc l'article complet, que la liste ne porte
  // pas (ses médias y sont réduits à leur nom et leur taille).
  const ouvrirApercu = async (entry) => {
    setPreview(entry);
    if (entry.builtin || entry.medias?.some((m) => m.contenu)) return;
    const complet = await lireArticleComplet(entry.filename);
    if (complet?.medias?.length) setPreview((p) => (p && p.filename === entry.filename ? { ...p, medias: complet.medias } : p));
  };

  const remove = async (entry) => {
    if (!window.confirm(`Retirer « ${entry.name} » du magasin ?`)) return;
    setUserSeeds(await retirerArticle(entry.filename));
  };

  return (
    <div
      className="seed-store"
      onDragOver={(e) => { e.preventDefault(); setSurvol(true); }}
      onDragLeave={() => setSurvol(false)}
      onDrop={onDrop}
      style={survol ? { outline: '2px dashed #2563eb', outlineOffset: 6, borderRadius: 8 } : undefined}
    >
      {survol && (
        <div className="muted" style={{ padding: '6px 0', color: '#2563eb', fontWeight: 600 }}>
          Déposez le fichier .json ici pour l'ajouter au magasin
        </div>
      )}
      <div className="seed-store__bar">
        <div>
          <h2 style={{ margin: 0 }}>Magasin de seeds</h2>
          <p className="muted" style={{ margin: '2px 0 0' }}>
            {catalog.length} article{catalog.length > 1 ? 's' : ''} — appliquer sur un site, rapatrier un site, télécharger, ou <b>glisser un .json ici</b> pour l'ajouter.
          </p>
        </div>
        <span className="spacer" style={{ flex: 1 }} />
        <button onClick={captureActive} title="Backup complet (styles, thèmes, affichage, services…) du site ciblé">⤓ Sauvegarder ce site (backup)</button>
        <button onClick={() => fileRef.current?.click()}>＋ Importer un seed…</button>
        <input
          ref={fileRef} type="file" accept="application/json,.json"
          style={{ display: 'none' }}
          onChange={(e) => importFile(e.target.files?.[0])}
        />
      </div>

      {status && <div className="status">{status}</div>}

      <div className="seed-grid">
        {catalog.map((entry) => (
          <article
            key={entry.filename}
            className="seed-card"
            style={{ borderTop: `4px solid ${entry.primaryColor}` }}
          >
            <ApercuArticle data={entry.data} medias={entry.medias} hauteur={132} />
            <header className="seed-card__head" style={{ marginTop: 8 }}>
              <strong>{entry.name}</strong>
              {entry.nameAr && <span className="muted" dir="rtl" style={{ display: 'block' }}>{entry.nameAr}</span>}
            </header>
            {entry.description && <p className="seed-card__desc muted">{entry.description}</p>}
            <div className="seed-card__badges">
              <span className="badge">{entry.servicesCount} service{entry.servicesCount > 1 ? 's' : ''}</span>
              <span className="badge">{entry.countersCount} guichet{entry.countersCount > 1 ? 's' : ''}</span>
              {entry.builtin ? <span className="badge badge--muted">livré</span> : <span className="badge badge--user">ajouté</span>}
            </div>
            <footer className="seed-card__actions">
              <button onClick={() => ouvrirApercu(entry)} className="ghost">Aperçu</button>
              <button onClick={() => downloadJson(entry.filename, entry.data)} className="ghost">⤓ Télécharger</button>
              <button onClick={() => apply(entry)} disabled={busy === entry.filename} title="Écrit ce seed dans la config active du serveur ciblé (PUT /api/config)">
                {busy === entry.filename ? '… envoi' : '⤒ Envoyer au serveur'}
              </button>
              {!entry.builtin && <button onClick={() => remove(entry)} className="ghost danger">✕</button>}
            </footer>
          </article>
        ))}
      </div>

      {preview && (
        <div className="seed-modal" onClick={() => setPreview(null)}>
          <div className="seed-modal__panel" onClick={(e) => e.stopPropagation()}>
            <header className="seed-modal__head" style={{ borderBottom: `3px solid ${preview.primaryColor}` }}>
              <div>
                <strong>{preview.name}</strong>
                {preview.nameAr && <span className="muted" dir="rtl" style={{ display: 'block' }}>{preview.nameAr}</span>}
              </div>
              <button className="ghost" onClick={() => setPreview(null)}>✕</button>
            </header>
            <div className="seed-modal__body">
              <ApercuArticle data={preview.data} medias={preview.medias} hauteur={300} />
              <p className="muted" style={{ margin: '6px 0 12px' }}>
                Rendu de l'écran d'affichage tel que cet article le produira : couleurs, logo,
                langues dans l'ordre réglé, et proportion de la colonne des tickets.
              </p>
              <h4>Services ({preview.servicesCount})</h4>
              <ul className="seed-svc-list">
                {preview.services.map((s) => (
                  <li key={s.id}>
                    <span className="swatch" style={{ background: s.color || '#ccc' }} />
                    <code>{s.id}</code> — {s.name}
                    {s.nameAr && <span className="muted" dir="rtl"> · {s.nameAr}</span>}
                    {s.nameEn && <span className="muted"> · {s.nameEn}</span>}
                  </li>
                ))}
              </ul>
              <h4>Guichets : {preview.countersCount}</h4>
            </div>
            <footer className="seed-modal__foot">
              <button className="ghost" onClick={() => downloadJson(preview.filename, preview.data)}>⤓ Télécharger</button>
              <button onClick={() => { const e = preview; setPreview(null); apply(e); }}>⤒ Envoyer au serveur</button>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
}
